<script setup lang="ts">
import type { Speciality } from '@mui/types/courses';
import { SpecialityLabels } from '@mui/types/courses';
import { SubscriptionType } from '~/composables/useAuthentication';
import { CourseChapterType, CourseContentActivityType } from "~/enums/course.enums";
import useNormalizedThumbnailUrl from "~/composables/useNormalizedThumbnailUrl";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import IconCME from 'assets/images/icons/icon-cme.svg';
import {
  KickoffMasteryTrackingEvents,
  KickoffMasteryTrackingParameters,
  KickoffMasteryTrackingValues,
} from "~/enums/tracking/kickoff-mastery.enums";

const emit = defineEmits<{
  (e: 'showActivityInformation'): void;
  (e: 'toggleBookmarkCourse'): void;
}>();

const { course, isCourse, isWorkshop, hasProgress } = useCourseState();
const { user } = useUserStore();
const { getImageUrl } = useCmsMedia();
const { dispatchTrackingEvent } = useTracking();
const courseTracking = useTrackingCourse();

const isShowingTrailerVideoPlayer = ref(false);
const isShowingFullLearningGoals = ref(false);

const chapters = computed(() => [
  ...course.value?.chapters.kickoff || [],
  ...course.value?.chapters.regular || [],
  ...course.value?.chapters.mastery || [],
]);

const lessons = computed(() => chapters.value?.reduce((carry, current) => {
  return carry + current.contents.filter(content => content.type === CourseContentActivityType.Lesson).length;
}, 0));

const quizzes = computed(() => chapters.value?.reduce((carry, current) => {
  return carry + current.contents.filter(content => content.type === CourseContentActivityType.Quiz).length;
}, 0));

const numberOfCases = computed(() => chapters.value?.filter(chapter => ![CourseChapterType.Kickoff, CourseChapterType.Mastery].includes(chapter.type as CourseChapterType))?.length);

const durationMinutes = computed(() => course.value?.duration || 0);

const trailerVimeoId = computed(() => {
  return course.value?.trailer?.source.split('vimeo.com/').pop() || '';
});

const durationPretty = computed(() => {
  const formatHours = (h: number) => `${h}h`;
  const formatMinutes = (m: number) => `${m}m`;

  if (!durationMinutes.value || typeof durationMinutes.value !== 'number') {
    return '';
  }

  const hours = Math.floor(durationMinutes.value / 60);
  if (hours) {
    const remaining = durationMinutes.value - (hours * 60);
    return remaining ? formatHours(hours) + ' ' + formatMinutes(remaining) : formatHours(hours);
  }

  return formatMinutes(durationMinutes.value);
});

const showFreeCmeBanner = computed(() => {
  const isFreeCmeCourse = course.value?.isFreeCmeCourse;
  const subscriptionType = user.value?.subscription.type;

  return isFreeCmeCourse &&
      [SubscriptionType.basic, SubscriptionType.trial].includes(subscriptionType as SubscriptionType);
});

const getSpecialityLabel = (speciality: Speciality) => {
  return SpecialityLabels[speciality] || speciality;
};

const specialtyClass = (speciality: Speciality) => {
  const specialityFormatted = (speciality ?? '').toLowerCase().replaceAll(' ', '-');
  return `spec-color-${specialityFormatted} spec-bgcolor-${specialityFormatted}-l`;
};

const nextActivity = computed(() => {
  if (!course.value) {
    return null;
  }

  const contents = chapters.value?.flatMap(chapter => chapter.contents) ?? [];

  return contents.find(content => {
    const isQuizType = [
      CourseContentActivityType.Quiz,
      CourseContentActivityType.KickoffQuiz,
      CourseContentActivityType.MasteryQuiz,
    ].includes(content.type);

    return isQuizType ? !content.progress?.finished : !content?.completed;
  }) ?? contents[0];
});

const handleCourseAction = () => {
  if (course.value?.completed) {
    return navigateTo(`/courses/${course.value?.id}/summary`);
  }

  if (!nextActivity.value) {
    return;
  }

  dispatchTrackingEvent(KickoffMasteryTrackingEvents.CourseStarted, {
    [KickoffMasteryTrackingParameters.CourseStartSource]: KickoffMasteryTrackingValues.StartLearningButton,
  });

  if ([CourseContentActivityType.Quiz, CourseContentActivityType.KickoffQuiz, CourseContentActivityType.MasteryQuiz].includes(nextActivity.value.type)) {
    return navigateTo(`/courses/${course.value?.id}/quizzes/${nextActivity.value.id}`);
  } else if (nextActivity.value.type === CourseContentActivityType.Lesson) {
    return navigateTo(`/courses/${course.value?.id}/lessons/${nextActivity.value.id}`);
  } else {
    console.error('Unknown content type');
  }
};

const formattedTeachersList = computed(() => {
  if (!course.value?.teachers) {
    return 'unknown';
  }

  const teachers: string[] = course.value.teachers.filter(teacher => teacher.name.length !== 0).map(teacher => teacher.name);

  // if the array is empty, return an empty string
  if (teachers.length === 0) {return '';}

  // if the array has one item, return that item
  if (teachers.length === 1) {return teachers[0];}

  // if the array has two items, join them with " and "
  if (teachers.length === 2) {return teachers[0] + ' and ' + teachers[1];}

  // if the array has more than two items
  const allButLast = teachers.slice(0, -1).join(', ');
  const last = teachers[teachers.length - 1];
  return `${allButLast}, and ${last}`;
});

const showUpsellCard = computed(() =>
  user.value && user.value?.subscription.type === SubscriptionType.trial && !course.value?.isFreeCmeCourse,
);

/*
 * learning goals (mobile)
 */
const isLearningGoalsExpanded = ref(false);
const showToggleLearningGoals = computed(() => (course.value?.learningGoals?.length ?? 0) > 4);
const displayedLearningGoals = computed(() => {
  return isLearningGoalsExpanded.value
    ? course.value?.learningGoals ?? []
    : (course.value?.learningGoals ?? []).slice(0, 4);
});
</script>

<template>
  <div
    v-if="showFreeCmeBanner"
    class="free-cme-banner flex bg-green-40 md:bg-transparent lg:my-12 md:my-6 justify-between items-center"
  >
    <div class="flex flex-row justify-between items-center w-full p-4 md:p-6 px-0 md:px-6 md:mt-8 md:rounded-2xl border border-neutral-20 bg-green-40 md:mx-auto">
      <div class="flex-grow max-w-[60%] text-neutral-90">
        <div class="title text-2xl font-bold mb-4 text-20 md:text-32 capitalize">
          Free CME {{course?.type ?? 'course'}} Unlocked For You!
        </div>
        <div class="description text-12 md:text-20 leading-130 md:leading-normal">
          See what it feels like to be a Medmastery Pro member with full access and earn free CME / CEU credits when you complete this {{course?.type ?? 'course'}}.
        </div>
      </div>
      <div class="flex-shrink-0">
        <img
          src="@/assets/images/gifs/gift.gif"
          class="max-h-24	md:max-h-32 aspect-square"
          alt="Free CME course animation"
        >
      </div>
    </div>
  </div>

  <article class="course-details lg:py-12 py-6">
    <header>
      <div>
        <h1 class="text-32 mb-1">
          {{course?.courseTitle}}
        </h1>
        <div class="mb-6">
          <div class="text-neutral-50 mb-2">
            by {{formattedTeachersList}}
          </div>

          <NuxtLink :to="`/courses?speciality=${course?.specialization}`">
            <Tag
              :class="specialtyClass(course?.specialization as Speciality)"
              class="uppercase"
              :value="getSpecialityLabel(course?.specialization as Speciality)"
            />
          </NuxtLink>
        </div>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div
          v-if="course && course.trailer"
          class="relative group"
        >
          <div class="aspect-video rounded-2xl cursor-pointer overflow-hidden">
            <img
              v-if="!isShowingTrailerVideoPlayer"
              class="w-full h-full"
              :src="useNormalizedThumbnailUrl(course.trailer.metaData.thumbnailUrl)"
              :alt="`${course.courseTitle} introduction video.`"
              @click="isShowingTrailerVideoPlayer = true"
            >

            <LessonPlayer
              v-else
              class="w-full h-full"
              :vimeo-id="trailerVimeoId"
              @ended="isShowingTrailerVideoPlayer = false"
            />
          </div>

          <Tag
            v-if="!isShowingTrailerVideoPlayer"
            :class="[
              'absolute top-4 right-4 flex items-center gap-2 px-3',
              'text-white transition-all',
              'bg-neutral-100/50 text-16 font-normal rounded-full pointer-events-none'
            ]"
          >
            <FontAwesomeIcon icon="fa-play" />
            Watch intro
          </Tag>
        </div>

        <div>
          <div class="text-14 uppercase tracking-widest text-neutral-90 font-bold mb-2">
            {{course?.type}} Description
          </div>

          <!--eslint-disable vue/no-v-html-->
          <div
            class="mb-4 text-neutral-70 leading-150 text-18"
            v-html="course?.description"
          />
          <!--eslint-enable-->

          <div class="grid grid-cols-2 gap-4 mb-4 lg:flex text-16 text-neutral-90">
            <div
              v-if="isCourse"
              class="flex items-center gap-1 "
            >
              <div class="min-h-[16px] max-h-[16px] h-[16px] flex ">
                <FontAwesomeIcon icon="fa-book" />
              </div>
              <div class="leading-[16px]">
                {{lessons}} Lessons
              </div>
            </div>

            <div
              v-if="isWorkshop"
              class="flex items-center gap-1"
            >
              <div class="min-h-[16px] max-h-[16px] h-[16px] flex ">
                <FontAwesomeIcon icon="fa-stethoscope" />
              </div>
              <div class="leading-[16px]">
                {{numberOfCases}} Cases
              </div>
            </div>

            <div
              v-if="durationPretty && isCourse"
              class="flex items-center gap-1"
            >
              <div class="min-h-[16px] max-h-[16px] h-[16px] flex ">
                <FontAwesomeIcon icon="fa-circle-play" />
              </div>
              <div class="leading-[16px]">
                {{durationPretty}}
              </div>
            </div>

            <div
              v-if="quizzes && isCourse"
              class="flex items-center gap-1 "
            >
              <div class="min-h-[16px] max-h-[16px] h-[16px] flex ">
                <FontAwesomeIcon icon="fa-bolt" />
              </div>
              <div class="leading-[16px]">
                {{quizzes}} {{`Quiz${quizzes !== 1 ? 'zes' : ''}`}}
              </div>
            </div>

            <div
              v-if="quizzes"
              class="flex items-center gap-1 "
            >
              <div class="min-h-[16px] max-h-[16px] h-[16px] flex ">
                <IconCME class="w-[16px] h-[16px]" />
              </div>
              <div
                v-if="course"
                class="leading-[16px]"
              >
                {{course.cmeCredits}} CME {{course.cmeCredits === 0 || course?.cmeCredits > 1 ? 'credits' : 'credit'}}
              </div>
            </div>
          </div>

          <div class="flex flex-wrap gap-4">
            <Button
              class="font-bold lg:!text-22 !text-20 text-nowrap flex gap-4 px-12 bg-blue-30 text-neutral-90 border-none rounded-full justify-center"
              @click="handleCourseAction"
            >
              <FontAwesomeIcon :icon="course?.completed ? 'fa-medal' : 'fa-play'" />

              <template v-if="course?.completed">
                See results
              </template>

              <template v-else>
                {{hasProgress ? 'Continue' : 'Start'}} learning
              </template>
            </Button>

            <BookmarkButton />
          </div>
        </div>
      </div>

      <Divider
        v-if="!showUpsellCard"
        class="lg:mb-0 mt-8"
      />
    </header>

    <Card
      v-if="showUpsellCard"
      class="upsell shadow-none my-8 rounded-2xl !bg-yellow-50"
      :pt="{
        body: {
          class: 'p-0'
        },
        content: {
          class: 'p-0'
        }
      }"
    >
      <template #content>
        <div class="text-24 font-bold text-neutral-90 mb-4">
          Unlock this course and over 100 more
        </div>

        <div class="text-18 text-neutral-90 mb-6">
          Get access to all our award-winning courses and workshops and become a great clinician.
        </div>

        <NuxtLink to="/pricing">
          <Button
            outlined
            severity="contrast"
            class="rounded-lg border-2 py-2.5 px-6 text-neutral-90"
          >
            <span class="text-20 font-bold">
              Get full access
            </span>
          </Button>
        </NuxtLink>
      </template>
    </Card>

    <div class="lg:grid grid-cols-2 lg:justify-between lg:gap-0 flex flex-col gap-8">
      <aside class="course-info md:pt-0 pt-6 flex flex-col gap-8">
        <div>
          <div class="text-14 uppercase tracking-widest text-neutral-90 font-bold mb-4">
            Teachers in this course
          </div>

          <div class="-mx-4">
            <NuxtLink
              v-for="(teacher) of course?.teachers || []"
              :key="teacher.name"
              :to="teacher.url"
            >
              <Button
                :class="[
                  'w-full rounded-xl bg-white hover:bg-neutral-10 text-neutral-90 border-none pl-4',
                  'flex items-center gap-4'
                ]"
              >
                <Avatar
                  class="outline-offset-2 outline outline-neutral-20"
                  size="xlarge"
                  shape="circle"
                >
                  <img
                    :src="getImageUrl(teacher.profilePicture)"
                    class="w-16 h-16 object-cover"
                    alt="Profile picture"
                  >
                </Avatar>

                <div class="flex flex-col text-left gap-2">
                  <div class="text-16 font-bold text-neutral-70">
                    {{teacher.name}}
                  </div>

                  <!-- eslint-disable vue/no-v-html -->
                  <div
                    class="text-neutral-60"
                    v-html="teacher.body"
                  />
                <!-- eslint-enable vue/no-v-html -->
                </div>
              </Button>
            </NuxtLink>
          </div>
        </div>

        <Divider class="lg:hidden flex m-0" />

        <div>
          <div class="text-14 uppercase tracking-widest text-neutral-90 font-bold mb-4">
            Course resources
          </div>

          <div
            v-if="course"
            class="-mx-4"
          >
            <template
              v-for="(resource, i) in course?.learningResources"
              :key="i"
            >
              <CourseSidebarTabsResourcesItem
                :resource="resource"
                :course="course"
                @downloaded="(resource) => courseTracking.resourceDownloaded(course, resource)"
              />
            </template>
          </div>
          <div
            v-if="!course?.learningResources?.length"
            class="text-14 text-neutral-60"
          >
            No course resources available.
          </div>
        </div>

        <Divider class="lg:hidden flex m-0" />

        <div v-if="course && course.cmeCredits > 0">
          <div class="text-14 uppercase tracking-widest text-neutral-90 font-bold mb-6">
            CME credits
          </div>

          <div class="flex gap-4">
            <div class="w-[3.25em]">
              <img
                src="~/assets/images/illustrations/diploma.png"
                alt="CME info"
                class="w-full h-auto"
              >
            </div>

            <div>
              This activity offers a maximum of {{course?.cmeCredits?.toFixed(1)}} credits.
              See the <span
                class="cursor-pointer hover:underline text-blue-60"
                @click="emit('showActivityInformation')"
              >activity information</span> for more details.
            </div>
          </div>

          <Divider class="lg:hidden flex m-0 mt-8" />
        </div>

        <div
          :class="{
            'learning-goals': true,
            'expanded': isShowingFullLearningGoals
          }"
          @click="isShowingFullLearningGoals = !isShowingFullLearningGoals"
        >
          <div class="text-14 uppercase tracking-widest text-neutral-90 font-bold mb-6">
            Learning goals
          </div>

          <div
            v-if="!course?.learningGoals?.length"
            class="flex gap-2 items-center"
          >
            <FontAwesomeIcon
              icon="fa-xmark"
              class="text-24"
            />
            <div>
              No learning goals
            </div>
          </div>

          <div class="lg:flex hidden flex-col gap-4">
            <div
              v-for="item in (course?.learningGoals ?? [])"
              :key="item"
              class="flex gap-2 items-center"
            >
              <FontAwesomeIcon
                icon="fa-circle-check"
                class="text-24"
              />
              <div>{{item}}</div>
            </div>
          </div>

          <div class="lg:hidden flex flex-col gap-4">
            <div
              v-for="item in displayedLearningGoals"
              :key="item"
              class="flex gap-2 items-center"
            >
              <FontAwesomeIcon
                icon="fa-circle-check"
                class="text-24"
              />
              <div>{{item}}</div>
            </div>

            <div
              v-if="showToggleLearningGoals"
              class="flex justify-center"
            >
              <Button
                text
                :label="isLearningGoalsExpanded ? 'Show less' : 'Show more'"
                @click="isLearningGoalsExpanded = !isLearningGoalsExpanded"
              />
            </div>
          </div>
        </div>

        <Divider class="lg:hidden flex m-0" />
      </aside>

      <div>
        <Divider class="lg:flex hidden m-0" />

        <section class="lg:pl-8 lg:pt-8">
          <div class="text-14 uppercase tracking-widest text-neutral-90 font-bold">
            What you will learn in this course
          </div>
          <CoursePlaylist :all-chapters-default-expanded="true" />
        </section>
      </div>
    </div>
  </article>
</template>

<style lang="scss">
@import '@mui/assets/styles/utility.scss';
@import '@mui/assets/styles/breakpoints.scss';

.free-cme-banner {
  @include container-sizer(page);
}

.course-details {
  @include container-sizer(page);

  .upsell {
    padding: 2em 1.5em 11em 1.5em;
    background-image: url('assets/images/illustrations/upsell/upsell-mobile.png');
    background-repeat: no-repeat;
    background-position: bottom;
    background-size: contain;

    @include breakpoint('small') {
      padding: 1.5em 55% 1.5em 1.5em;
      background-image: url('assets/images/illustrations/upsell/upsell-short-desktop.png');
      background-position: center right;
      text-align: left;
      background-size: contain;
    }

    // mobile retina override
    @media (-webkit-min-device-pixel-ratio: 1.5) and (min-resolution: 192dpi) {
      background-image: url('assets/images/illustrations/upsell/upsell-mobile@2x.png');
    }

    // desktop retina override
    @media (-webkit-min-device-pixel-ratio: 1.5) and (max-resolution: 192dpi) and (min-width: #{map-get($breakpoints, 'small')}) {
      background-image: url('assets/images/illustrations/upsell/upsell-short-desktop@2x.png');
    }
  }

  .course-info {
    @include breakpoint ('medium') {
      border-right: 1px solid var(--palette-neutral-20);
    }

    > * {
        @include breakpoint ('medium') {
          border-top: 1px solid var(--palette-neutral-20);
          padding: 2em 2em 0 0;
        }
      }
  }
}
</style>
